"use client";

import { ISiteFooterConfig } from "@/components/footers/SiteFooter";
import { ISiteHeaderConfig } from "@/components/headers/site-header/SiteHeader";
import { SiteFooterTypes } from "@/shared/enums/site-footer-types.enum";
import { SiteHeaderTypes } from "@/shared/enums/site-header-types.enum";
import { usePathname } from "next/navigation";
import { useContext, createContext, useState } from "react";

// Function to match current location with header configuration routes
const getItemType = (pathname: string, parentConfig: ISiteHeaderConfig[] | ISiteFooterConfig[]): any => {
  for (const config of parentConfig) {
    const { type, routes } = config;

    // Check if the current location matches any route with a wildcard
    for (const route of routes) {
      // Match the wildcard route
      const routeRegex = new RegExp(`^${route.replace("*", ".*")}$`);

      if (routeRegex.test(pathname)) {
        return type;
      }
    }
  }
};

export interface LayoutConfigContext {
  headerConfig: ISiteHeaderConfig[];
  footerConfig: ISiteFooterConfig[];
  defaultHeaderType: `${SiteHeaderTypes}`;
  defaultFooterType: `${SiteFooterTypes}`;
  getItemType: (pathname: string, parentConfig: ISiteHeaderConfig[] | ISiteFooterConfig[]) => any;
  getHeaderType: () => SiteHeaderTypes;
  getFooterType: () => SiteFooterTypes;
  setHeaderType: (data?: any) => void;
  setFooterType: (data?: any) => void;
}

const initialData: any = {
  headerConfig: undefined,
  footerConfig: undefined,
  defaultHeaderType: undefined,
  defaultFooterType: undefined,
  getHeaderType: () => {},
  setHeaderType: (data?: any) => {},
  getFooterType: () => {},
  setFooterType: (data?: any) => {}
};

// Create the context
const LayoutConfigContext = createContext<LayoutConfigContext>(initialData);

// Create a provider component
export const LayoutConfigProvider = ({
  children,
  config
}: {
  children: any;
  config: {
    headerConfig: ISiteHeaderConfig[];
    footerConfig: ISiteFooterConfig[];
    defaultHeaderType: `${SiteHeaderTypes}`;
    defaultFooterType: `${SiteFooterTypes}`;
  };
}) => {
  const pathname = usePathname();
  const [headerType, setHeaderType] = useState();
  const [footerType, setFooterType] = useState();

  const getHeaderType = () => headerType || getItemType(pathname, config?.headerConfig) || config?.defaultHeaderType;
  const getFooterType = () => footerType || getItemType(pathname, config?.footerConfig) || config?.defaultFooterType;

  const value: LayoutConfigContext = {
    ...config,
    getItemType,
    getHeaderType,
    setHeaderType,
    getFooterType,
    setFooterType
  };

  return <LayoutConfigContext.Provider value={value}>{children}</LayoutConfigContext.Provider>;
};

// Create a custom hook to use the context
export const useLayoutConfigProvider = () => {
  const context = useContext(LayoutConfigContext);

  if (!context) {
    throw new Error("useLayoutConfigProvider must be used within an LayoutConfigProvider");
  }

  return context;
};
